import { Currency } from '@ps/web-core-enums';
import React from 'react';
import { FormattedNumber, FormattedNumberParts } from 'react-intl';
import { useSelector } from 'react-redux';

import type { ExtraWalletCurrency } from '@swf/core/models/sports/extraWallets/types';
import { select } from '@swf/core/store';

export interface Props {
  value: number;
  currency?: Currency | ExtraWalletCurrency;
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code';
  /**
   * By default the value is expected in the currencies subunit, e.g. pence, cents, etc.
   * When false the value will be converted into the the currencies base unit, e.g. 1010 will output as $10.10.
   * When true the value will not be converted, e.g. 1010 will output as $1,010.
   * @default false
   */
  dollarValue?: boolean;
  isZeroExponentCurrency?: boolean;
  useGrouping?: boolean;
  style?: 'decimal' | 'currency';
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  children?(val: Intl.NumberFormatPart[]): React.ReactNode | null;
}

const CurrencyAmount: React.FC<Props> = ({
  value,
  currency,
  currencyDisplay,
  dollarValue = false,
  style = 'currency',
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  isZeroExponentCurrency = false,
  useGrouping = true,
  children
}) => {
  const realMoneyCurrency = useSelector(select.currency.getRealMoney);
  const dollarAmount = dollarValue ? value : value / 100;
  const props = {
    currency: currency || realMoneyCurrency,
    currencyDisplay,
    maximumFractionDigits: isZeroExponentCurrency ? 0 : maximumFractionDigits,
    minimumFractionDigits: isZeroExponentCurrency ? 0 : minimumFractionDigits,
    style,
    useGrouping,
    value: dollarAmount
  };

  if (currency === Currency.PlayMoney) {
    return (
      <FormattedNumber
        value={dollarAmount}
        minimumFractionDigits={minimumFractionDigits}
        maximumFractionDigits={maximumFractionDigits}
      />
    );
  } else if (children) {
    return (
      <FormattedNumberParts {...props}>
        {children as (val: Intl.NumberFormatPart[]) => React.ReactElement | null}
      </FormattedNumberParts>
    );
  } else {
    return <FormattedNumber {...props} />;
  }
};

export default CurrencyAmount;
